import { createSampleContentProject, createSampleQuoteProject, createSampleSafetyProject } from "./sample-companions";
import { createSampleMarketingProject } from "./sample-marketing";
import { sampleOrganizationTeams, sampleUserResources } from "./sample-org";
import { createSampleRobotProject } from "./sample-robot";
import { createSampleProject } from "./sample";
import type { OrganizationTeam, Project, UserResource } from "./types";

export type WorkspaceTemplate = "empty" | "erp" | "robot" | "marketing";

/** 이 계정이 그 워크스페이스에서 가진 역할. */
export type WorkspaceMembership = "owner" | "admin" | "editor" | "viewer";

export const DEFAULT_WORKSPACE_ICON = "building";

export interface WorkspaceSeedMeta {
  id: string;
  name: string;
  /** Fluent 팀 아이콘 id. 이미지가 없을 때 색 타일 위에 그린다. */
  icon: string;
  color: string;
  description: string;
  template: WorkspaceTemplate;
  /** 대표 이미지. 시드는 로컬 SVG다. */
  imageUrl: string;
  role: WorkspaceMembership;
  ownerName: string | null;
  ownerEmail: string | null;
}

/** 64 보기에도 남는 단순한 면. 글자는 넣지 않는다. */
function workspaceCover(background: string, body: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128"><rect width="128" height="128" fill="${background}"/>${body}</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

const COVER_WAVEX = workspaceCover(
  "#0078d4",
  `<rect x="22" y="58" width="22" height="48" fill="#fff"/><rect x="50" y="36" width="22" height="70" fill="#fff"/><rect x="78" y="22" width="28" height="84" fill="#fff" fill-opacity="0.92"/>`,
);
const COVER_ROBOT = workspaceCover(
  "#be3528",
  `<rect x="58" y="16" width="12" height="14" rx="2" fill="#fff"/><circle cx="64" cy="48" r="20" fill="#fff"/><rect x="36" y="74" width="56" height="32" rx="10" fill="#fff"/>`,
);
const COVER_GROWTH = workspaceCover(
  "#da5978",
  `<rect x="22" y="72" width="22" height="34" rx="3" fill="#fff"/><rect x="53" y="48" width="22" height="58" rx="3" fill="#fff"/><rect x="84" y="24" width="22" height="82" rx="3" fill="#fff"/>`,
);
const COVER_HANBIT = workspaceCover(
  "#107c41",
  `<rect x="34" y="24" width="60" height="82" fill="#fff"/><rect x="46" y="38" width="12" height="12" fill="#107c41"/><rect x="70" y="38" width="12" height="12" fill="#107c41"/><rect x="46" y="58" width="12" height="12" fill="#107c41"/><rect x="70" y="58" width="12" height="12" fill="#107c41"/><rect x="56" y="82" width="16" height="24" fill="#107c41"/>`,
);

export const SEED_WORKSPACE_META: WorkspaceSeedMeta[] = [
  {
    id: "ws_wavex",
    name: "웨이브엑스 SI",
    icon: "briefcase",
    color: "#0078d4",
    description: "제조 ERP·SI 구축",
    template: "erp",
    imageUrl: COVER_WAVEX,
    role: "owner",
    ownerName: null,
    ownerEmail: null,
  },
  {
    id: "ws_robot",
    name: "AI 로봇 공장",
    icon: "flash",
    color: "#be3528",
    description: "로봇 라인·비전 검사",
    template: "robot",
    imageUrl: COVER_ROBOT,
    role: "owner",
    ownerName: null,
    ownerEmail: null,
  },
  {
    id: "ws_growth",
    name: "그로스 마케팅",
    icon: "megaphone",
    color: "#da5978",
    description: "캠페인·CRM·실험",
    template: "marketing",
    imageUrl: COVER_GROWTH,
    role: "owner",
    ownerName: null,
    ownerEmail: null,
  },
];

/** 초대로 들어온 목업. 세 시드 목업을 다시 채울 때 플랜은 건드리지 않는다. */
export const SAMPLE_JOINED_WORKSPACE: WorkspaceSeedMeta = {
  id: "ws_hanbit",
  name: "한빛건설",
  icon: "building",
  color: "#107c41",
  description: "현장 일정",
  template: "empty",
  imageUrl: COVER_HANBIT,
  role: "viewer",
  ownerName: "한빛 소장",
  ownerEmail: "owner@hanbit.example",
};

export function seedWorkspaceFace(id: string): WorkspaceSeedMeta | null {
  return SEED_WORKSPACE_META.find((meta) => meta.id === id) ?? (SAMPLE_JOINED_WORKSPACE.id === id ? SAMPLE_JOINED_WORKSPACE : null);
}

export const WORKSPACE_COLORS = ["#0078d4", "#107c41", "#be3528", "#da5978", "#8764b8", "#008272", "#ca5010"] as const;

const ERP_TEAMS = ["team_pmo", "team_arch", "team_biz", "team_partner"];
const ERP_RESOURCES = ["res_me", "res_kim_pm", "res_lee_arch", "res_park_dev", "res_choi_plan", "res_jung_front", "res_kang_infra", "res_song_dba", "res_oh_qa", "res_shin_spec"];

const ROBOT_TEAMS = ["team_robot", "team_qa", "team_arch", "team_pmo"];
const ROBOT_RESOURCES = ["res_me", "res_han_robot", "res_na_vision", "res_baek_ctrl", "res_kang_infra", "res_lee_arch", "res_yoon_data", "res_seo_ml", "res_oh_qa", "res_min_qa", "res_shin_spec"];

const MARKETING_TEAMS = ["team_pmo", "team_growth", "team_data", "team_biz", "team_qa"];
const MARKETING_RESOURCES = ["res_me", "res_lim_growth", "res_kwon_crm", "res_jang_copy", "res_jung_front", "res_yoon_data", "res_seo_ml", "res_min_qa", "res_cho_partner"];

export interface WorkspaceCatalogData {
  projects: Record<string, Project>;
  resources: Record<string, UserResource>;
  teams: Record<string, OrganizationTeam>;
  activeProjectId: string | null;
}

function pick<T extends { id: string }>(items: T[], ids: readonly string[]): Record<string, T> {
  const allow = new Set(ids);
  const out: Record<string, T> = {};
  for (const item of items) {
    if (allow.has(item.id)) out[item.id] = item;
  }
  return out;
}

function withProjects(projects: Project[]): WorkspaceCatalogData["projects"] {
  const out: WorkspaceCatalogData["projects"] = {};
  for (const project of projects) out[project.id] = project;
  return out;
}

/** Isolated mock pack for one workspace. Owned templates have two plans. Empty has none. */
export function createWorkspaceCatalog(template: WorkspaceTemplate): WorkspaceCatalogData {
  if (template === "erp") {
    const primary = createSampleProject();
    const second = createSampleQuoteProject();
    return {
      projects: withProjects([primary, second]),
      resources: pick(sampleUserResources(), ERP_RESOURCES),
      teams: pick(sampleOrganizationTeams(), ERP_TEAMS),
      activeProjectId: primary.id,
    };
  }
  if (template === "robot") {
    const primary = createSampleRobotProject();
    const second = createSampleSafetyProject();
    return {
      projects: withProjects([primary, second]),
      resources: pick(sampleUserResources(), ROBOT_RESOURCES),
      teams: pick(sampleOrganizationTeams(), ROBOT_TEAMS),
      activeProjectId: primary.id,
    };
  }
  if (template === "marketing") {
    const primary = createSampleMarketingProject();
    const second = createSampleContentProject();
    return {
      projects: withProjects([primary, second]),
      resources: pick(sampleUserResources(), MARKETING_RESOURCES),
      teams: pick(sampleOrganizationTeams(), MARKETING_TEAMS),
      activeProjectId: primary.id,
    };
  }
  return { projects: {}, resources: {}, teams: {}, activeProjectId: null };
}

export function defaultWorkspaceIcon(): string {
  return DEFAULT_WORKSPACE_ICON;
}

export function defaultWorkspaceColor(index = 0): string {
  return WORKSPACE_COLORS[index % WORKSPACE_COLORS.length] ?? "#0078d4";
}
